import { useState } from 'react';
import { UnicodeEntity } from '@mews/enums';
import { lt } from '@mews/framework';
import { IconButton, IconName, Stack, StackItem } from '@optimus-web/core';
import { DividerList, LabeledListItem, ContentSection } from '@mews-commander/ui';
import { getCurrentEnterpriseDefaultLanguageCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { usePrivilege } from '@mews-commander/core';
import { ActorProfilesSection } from '../../../../../components/commander-ui';
import { useEditRateGroupDialogForm } from '../../forms';
import { useCurrentRateGroupContext } from '../../../contexts';
import { LanguageSwitcher } from './LanguageSwitcher';

export const GeneralTabContent = () => {
    const {
        externalIdentifier,
        descriptions,
        names,
        ordering,
        shortNames,
        creatorProfile,
        createdUtc,
        updaterProfile,
        updatedUtc,
    } = useCurrentRateGroupContext();

    const rateGroupLanguages = Object.keys(names);

    const defaultLanguageCode: string = useCommanderSelector(getCurrentEnterpriseDefaultLanguageCode);
    const [languageCode, setLanguageCode] = useState(
        rateGroupLanguages.includes(defaultLanguageCode) ? defaultLanguageCode : rateGroupLanguages[0]
    );

    const [renderDialog, toggleDialog] = useEditRateGroupDialogForm({ initialLanguageCode: languageCode });
    const canManageRateGroups = usePrivilege('manageRateGroups');

    return (
        <>
            <ContentSection
                title={lt('DetailPlural')}
                description={descriptions[languageCode] ?? UnicodeEntity.Dash}
                rightContent={
                    <Stack spacing="100">
                        <StackItem width="200px">
                            <LanguageSwitcher
                                onLanguageChange={setLanguageCode}
                                rateGroupLanguages={Object.keys(names)}
                                selectedLanguageCode={languageCode}
                            />
                        </StackItem>
                        {canManageRateGroups ? (
                            <IconButton icon={IconName.Edit} size="small" onClick={toggleDialog} type="submit" />
                        ) : null}
                    </Stack>
                }
            >
                <DividerList>
                    <LabeledListItem label={lt('Name')} contentOptions={{ wordBreak: 'break-all' }}>
                        {names[languageCode]}
                    </LabeledListItem>
                    <LabeledListItem label={lt('ShortName')}>
                        {shortNames[languageCode] ?? UnicodeEntity.Dash}
                    </LabeledListItem>
                    <LabeledListItem label={lt('RateGroupKey')}>
                        {externalIdentifier ?? UnicodeEntity.Dash}
                    </LabeledListItem>
                    <LabeledListItem label={lt('Ordering')}>{ordering}</LabeledListItem>
                </DividerList>
            </ContentSection>
            {creatorProfile && updaterProfile ? (
                <ActorProfilesSection
                    createdUtc={createdUtc}
                    creatorProfile={creatorProfile}
                    updatedUtc={updatedUtc}
                    updaterProfile={updaterProfile}
                />
            ) : null}
            {renderDialog()}
        </>
    );
};
